import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { CustomerService } from '../../services/customer.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { InventoryService } from '../../services/inventory.service';
import { PrintService } from '../../services/print.service';

@Component({
	selector: 'app-matrix-brands',
	templateUrl: './matrix-brands.component.html',
	styleUrls: ['./matrix-brands.component.scss']
})
export class MatrixBrandsComponent implements OnInit {
	@ViewChild('exportTable') exportTableRef: ElementRef;

	color = 'blue';
	customerControl = new FormControl('');
	customerSearchSub: any = null;
	sending = false;
	searchResults: any[] = [];
	selectedCustomer: any = null;

	brandData: any[] = [];
	allBrands: any[] = [];
	loading = false;
	exporting = false;

	copyFromControl = new FormControl('');
	copyFromResults: any[] = [];
	copyFromCustomer: any = null;
	showCopySection = false;

	cardFilter = '';
	bulkDiscountPercent = '';

	addBrandId = '';
	addDiscountPercent = '';
	showAddPanel = false;

	constructor(
		private location: Location,
		private globalSearchService: GlobalSearchService,
		private customerService: CustomerService,
		private inventoryService: InventoryService,
		private printService: PrintService
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.inventoryService.getBrands().subscribe((brands: any) => {
			this.allBrands = Array.isArray(brands) ? brands : (brands?.brands ?? []);
		});
	}

	back(): void {
		this.location.back();
	}

	onCustomerSelected(customer: any): void {
		this.selectedCustomer = customer;
		this.showAddPanel = false;
		this.cardFilter = '';
		this.loadBrandMatrix();
	}

	loadBrandMatrix(): void {
		if (!this.selectedCustomer?.debtorno) { return; }
		this.loading = true;
		this.customerService.getPriceMatrixBrands(this.selectedCustomer.debtorno).subscribe((res: any) => {
			this.loading = false;
			this.brandData = (res?.data ?? []).map((r: any) => ({
				...r,
				editValue: r.discount != null ? parseFloat((r.discount * 100).toFixed(4)) : 0
			}));
		}, () => { this.loading = false; });
	}

	customerSearch(keyword: string): void {
		if (this.customerSearchSub) { this.customerSearchSub.unsubscribe(); }
		if (!keyword || keyword.length < 1) { this.searchResults = []; return; }
		this.sending = true;
		this.customerSearchSub = this.customerService.getCustomerSearch({
			source: 'copymatrix',
			keywords: keyword
		}).subscribe((results: any) => {
			this.sending = false;
			this.searchResults = Array.isArray(results) ? results : [];
		}, () => { this.sending = false; });
	}

	displayCustomerFn(c: any): string {
		return c ? `${c.debtorno} ${c.name || ''}` : '';
	}

	filteredBrandData(): any[] {
		if (!this.cardFilter) { return this.brandData; }
		const f = this.cardFilter.toLowerCase();
		return this.brandData.filter((r: any) =>
			(r.brand_name || '').toLowerCase().includes(f) ||
			(r.brand || '').toLowerCase().includes(f)
		);
	}

	trackByBrand(_: number, row: any): string {
		return row.brand;
	}

	getCardClass(discount: any): string {
		const d = parseFloat(discount) * 100;
		if (isNaN(d) || d === 0) { return ''; }
		if (d > 0) { return 'brand-discounted'; }
		return 'brand-negative';
	}

	saveBrand(row: any): void {
		const raw = String(row.editValue);
		const num = raw === '' ? 0 : parseFloat(raw);
		if (isNaN(num) || num < -100 || num > 100) {
			this.globalSearchService.showNotification('Discount must be between -100 and 100', 'warning', 'bottom', 'right');
			return;
		}
		this.customerService.savePriceMatrixBrand({
			debtorno: this.selectedCustomer.debtorno,
			brand: String(row.brand),
			discount: num
		}).subscribe((res: any) => {
			if (res?.success) {
				row.discount = num / 100;
				row.editValue = num;
				this.globalSearchService.showNotification((row.brand_name || row.brand) + ' saved', 'success', 'bottom', 'right');
			} else {
				this.globalSearchService.showNotification(res?.message || 'Save failed', 'danger', 'bottom', 'right');
			}
		});
	}

	removeBrand(row: any): void {
		if (!this.selectedCustomer) { return; }
		this.customerService.deletePriceMatrixBrand({
			debtorno: this.selectedCustomer.debtorno,
			brand: String(row.brand)
		}).subscribe((res: any) => {
			if (res?.success) {
				this.globalSearchService.showNotification('Removed', 'success', 'bottom', 'right');
				this.brandData = this.brandData.filter((r: any) => r.brand !== row.brand);
			} else {
				this.globalSearchService.showNotification(res?.message || 'Delete failed', 'danger', 'bottom', 'right');
			}
		});
	}

	applyBulkDiscount(): void {
		const num = parseFloat(String(this.bulkDiscountPercent));
		if (!this.selectedCustomer || isNaN(num) || num < -100 || num > 100) {
			this.globalSearchService.showNotification('Enter a discount between -100 and 100', 'warning', 'bottom', 'right');
			return;
		}
		if (!this.brandData.length) {
			this.globalSearchService.showNotification('No brands to update', 'warning', 'bottom', 'right');
			return;
		}
		const rules = this.brandData.map((r: any) => ({ brand: r.brand, discount: num }));
		this.customerService.savePriceMatrixBrandsBulk({
			debtorno: this.selectedCustomer.debtorno,
			rules
		}).subscribe((res: any) => {
			if (res?.success) {
				this.globalSearchService.showNotification('Bulk update applied', 'success', 'bottom', 'right');
				this.bulkDiscountPercent = '';
				this.loadBrandMatrix();
			} else {
				this.globalSearchService.showNotification(res?.message || 'Bulk save failed', 'danger', 'bottom', 'right');
			}
		});
	}

	addBrand(): void {
		if (!this.selectedCustomer || !this.addBrandId) { return; }
		const raw = String(this.addDiscountPercent);
		const num = raw === '' ? 0 : parseFloat(raw);
		if (isNaN(num) || num < -100 || num > 100) {
			this.globalSearchService.showNotification('Discount must be between -100 and 100', 'warning', 'bottom', 'right');
			return;
		}
		const brandName = (this.allBrands.find((b: any) => String(b.manufacturer_id) === String(this.addBrandId))?.name) || this.addBrandId;
		this.customerService.savePriceMatrixBrand({
			debtorno: this.selectedCustomer.debtorno,
			brand: String(this.addBrandId),
			discount: num
		}).subscribe((res: any) => {
			if (res?.success) {
				const newRow = {
					brand: this.addBrandId,
					brand_name: brandName,
					discount: num / 100,
					editValue: num
				};
				this.brandData = [...this.brandData, newRow];
				this.globalSearchService.showNotification(brandName + ' added', 'success', 'bottom', 'right');
				this.addBrandId = '';
				this.addDiscountPercent = '';
				this.showAddPanel = false;
			} else {
				this.globalSearchService.showNotification(res?.message || 'Save failed', 'danger', 'bottom', 'right');
			}
		});
	}

	brandsNotInMatrix(): any[] {
		const existing = new Set(this.brandData.map((r: any) => String(r.brand)));
		return this.allBrands.filter((b: any) => !existing.has(String(b.manufacturer_id)));
	}

	copyFromSearch(keyword: string): void {
		if (!keyword || keyword.length < 1) { this.copyFromResults = []; return; }
		this.customerService.getCustomerSearch({ source: 'copymatrix', keywords: keyword }).subscribe((results: any) => {
			this.copyFromResults = Array.isArray(results) ? results : [];
		});
	}

	onCopyFromSelected(customer: any): void {
		this.copyFromCustomer = customer;
	}

	runCopyBrands(): void {
		if (!this.selectedCustomer || !this.copyFromCustomer) { return; }
		this.customerService.copyPriceMatrixBrands({
			from: this.copyFromCustomer.debtorno,
			to: this.selectedCustomer.debtorno
		}).subscribe((res: any) => {
			if (res?.success) {
				this.globalSearchService.showNotification('Brand matrix copied', 'success', 'bottom', 'right');
				this.showCopySection = false;
				this.copyFromCustomer = null;
				this.copyFromControl.setValue('');
				this.loadBrandMatrix();
			} else {
				this.globalSearchService.showNotification(res?.message || 'Copy failed', 'danger', 'bottom', 'right');
			}
		});
	}

	exportXls(): void {
		if (!this.exportTableRef || !this.brandData.length) { return; }
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.exportTableRef.nativeElement.innerHTML);
		const data = {
			content: encoded,
			filename: 'BrandMatrix-' + this.selectedCustomer.debtorno,
			title: 'Customer Brand Matrix — ' + this.selectedCustomer.debtorno + ' ' + (this.selectedCustomer.name || ''),
			subtitle: 'Exported ' + new Date().toLocaleString()
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.exporting = false;
			this.globalSearchService.downloadXls(result.content, data.filename);
		}, () => { this.exporting = false; });
	}

	exportPdf(): void {
		if (!this.exportTableRef || !this.brandData.length) { return; }
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.exportTableRef.nativeElement.innerHTML);
		const data = {
			content: encoded,
			filename: 'BrandMatrix-' + this.selectedCustomer.debtorno,
			title: 'Customer Brand Matrix — ' + this.selectedCustomer.debtorno + ' ' + (this.selectedCustomer.name || ''),
			subtitle: 'Exported ' + new Date().toLocaleString(),
			landscape: false
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.exporting = false;
			this.globalSearchService.downloadPdf(result.content, data.filename);
		}, () => { this.exporting = false; });
	}
}
